import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	LocationResource,
	LocationType,
	VelavuAPI,
	VelavuSite,
} from "velavu-js-api";
import {
	requestAddressReverseGeocoding,
	requestCityReverseGeocoding,
	useMapboxAPIKey,
} from "../../../helper/api-helper";
import { getLocationFloorID } from "../../../helper/floor-helper";
import { useCancellableEffect } from "../../../helper/hook-helper";

export enum LocationDescriptionState {
	Loading,
	Error,
	Unavailable,
	OK,
}

export type LocationDescription =
	| {
			state:
				| LocationDescriptionState.Loading
				| LocationDescriptionState.Error
				| LocationDescriptionState.Unavailable;
	  }
	| {
			state: LocationDescriptionState.OK;
			lines: string[];
	  };

export interface Locatable {
	site_id?: string;
	location?: LocationResource;
}

/**
 * Fetches a multi-line description of a location
 */
export function useLocationDescription(
	locatable: Locatable | undefined,
	site?: VelavuSite,
): LocationDescription {
	const { t, i18n } = useTranslation();
	const mapboxKey = useMapboxAPIKey();
	const mapboxLanguage = i18n.language;
	const cachedSiteRef = useRef<VelavuSite>(null);

	const [description, setDescription] = useState<LocationDescription>({
		state: LocationDescriptionState.Loading,
	});

	useCancellableEffect(
		(addPromise) => {
			if (locatable === undefined) {
				setDescription({
					state: LocationDescriptionState.Unavailable,
				});
				return;
			}

			setDescription({
				state: LocationDescriptionState.Loading,
			});

			addPromise(
				(async (): Promise<LocationDescription> => {
					//Cellular isn't very accurate, so only look up
					//the approximate location
					const approximate =
						locatable?.location?.location_type ===
						LocationType.Cellular;

					//Attempt to look up the device's site
					if (!approximate && locatable.site_id !== undefined) {
						try {
							let targetSite: VelavuSite;
							if (site !== undefined) {
								targetSite = site;
							} else if (
								cachedSiteRef.current?.id === locatable.site_id
							) {
								targetSite = cachedSiteRef.current;
							} else {
								targetSite =
									await VelavuAPI.sites.getSpecificSite(
										locatable.site_id,
									);
								cachedSiteRef.current = targetSite;
							}

							const floorID = getLocationFloorID(
								locatable.location,
							);
							let floorName: string;
							if (floorID !== undefined) {
								const floor = targetSite.floors?.find(
									(f) => f.id === floorID,
								);
								if (floor !== undefined) {
									floorName = floor.name;
								} else {
									floorName = t(
										"dashboard.location.unknown_floor",
									);
								}
							} else {
								floorName = t("dashboard.location.no_floor");
							}

							return {
								state: LocationDescriptionState.OK,
								lines: [targetSite.name, floorName],
							};
						} catch (error) {
							console.warn(
								`Failed to fetch site ${locatable.site_id}`,
								error,
							);
							//Fall through and try device address
						}
					}

					//Attempt to look up the device's address
					if (locatable.location !== undefined) {
						//Get the Mapbox token
						if (mapboxKey === undefined) {
							console.warn("Geocoder failed; no Mapbox token");
							return {
								state: LocationDescriptionState.Error,
							};
						}

						const coordinates = locatable.location.coordinates;

						//Fetch the asset location
						try {
							if (approximate) {
								const cityResult =
									await requestCityReverseGeocoding(
										mapboxKey,
										mapboxLanguage,
										coordinates,
									);
								if (cityResult === undefined) {
									return {
										state: LocationDescriptionState.Error,
									};
								} else {
									return {
										state: LocationDescriptionState.OK,
										lines: [
											t(
												"api_helper.approximate_location",
												{ location: cityResult },
											),
										],
									};
								}
							} else {
								const addressResult =
									await requestAddressReverseGeocoding(
										mapboxKey,
										mapboxLanguage,
										coordinates,
									);

								if (addressResult === undefined) {
									return {
										state: LocationDescriptionState.Error,
									};
								} else {
									return {
										state: LocationDescriptionState.OK,
										lines: [addressResult],
									};
								}
							}
						} catch (error) {
							console.log(error);

							//Fall back to geo coordinates if address couldn't be loaded
							return {
								state: LocationDescriptionState.OK,
								lines: [coordinates.join(", ")],
							};
						}
					}

					//No way to determine the device's location
					return {
						state: LocationDescriptionState.Unavailable,
					};
				})(),
			)
				.then(setDescription)
				.catch(console.warn);
		},
		[locatable, site, mapboxKey, mapboxLanguage, t],
	);

	return description;
}
